import React, { useEffect, useMemo, useState } from "react";
import { qrEventsApi } from "../../api/qrEventsApi";

// MUI
import {
    Box,
    Checkbox,
    Chip,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    TextField,
    Tooltip,
    Typography, CircularProgress,
} from "@mui/material";
import { notify } from "../../../../util/toast";
import { confirm as confirmDialog } from "../../../../util/confirmDialog";
import FormModal from "../../../../component/common/FormModal";
import ActionButton from "../../../../component/common/ActionButton";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import InputAdornment from "@mui/material/InputAdornment";
import { formatDateTime } from "../../../../util/dateUtils";
import { formatPhoneDisplay } from "../../../../util/phone";
import { ds } from "../../../../styles/designSystem";
import FormField from "../../../../component/common/FormField";
import AppAutocomplete from "../../../../component/common/AppAutocomplete";
import { runApiAction, serverMsgOr } from "../../../../util/apiFeedback";
import TablePaginator, { TableSizeSelect } from "../../../../component/common/TablePaginator";

// 한 페이지에 보여줄 인원 — 행사 명단은 수백~수천 명이라 크게도 볼 수 있게 한다
const PAGE_SIZE_OPTIONS = [100, 300, 500, 1000, 2000, 3000];

const emptyParticipant = {
    privateCode: "",
    name: "",
    affiliation: "",
    gender: "",
    contactNumber: "",
    email: "",
    attend_time: "",
};

/**
 * ✅ 상위에서 행사명을 내려주면(selectedEventFromParent) 자동으로 행사 선택 + 참가자 로드
 * - onSelectedEventChange: 사용자가 드롭다운에서 행사 바꿀 때 상위에도 반영
 * - 행 클릭 → 수정 모달(삭제도 모달 안에서). 좌측 체크박스로 골라 선택 삭제.
 */
export default function ParticipantTable({
                                             selectedEventFromParent = "",
                                             onSelectedEventChange,
                                         }) {
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState("");
    const [participants, setParticipants] = useState([]);

    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages]   = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingParticipants, setLoadingParticipants] = useState(false);

    // 참가자 모달
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState("add"); // add | edit
    const [currentParticipant, setCurrentParticipant] = useState(emptyParticipant);

    // xlsx 업로드 모달
    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const [file, setFile] = useState(null);
    const [uploadingXlsx, setUploadingXlsx] = useState(false);

    // 참가자 검색 — 이름·이메일·소속·연락처·직함 부분일치 (현재 페이지 내)
    const [participantSearch, setParticipantSearch] = useState("");

    // 좌측 체크박스 선택 → 선택 삭제. 참가자 id 로 고른다(개인코드는 행사마다 겹칠 수 있다).
    const [selected, setSelected] = useState(() => new Set());


    const fetchEvents = async () => {
        setLoadingEvents(true);
        try {
            const response = await qrEventsApi.getEvents();
            setEvents(Array.isArray(response) ? response : (response?.data || response || []));
        } catch (error) {
            console.error("행사를 가져오는 중 오류", error);
            if (error.message?.includes("No permission") || error.message?.includes("권한")) {
                notify("행사 목록 확인 권한이 없습니다", "error");
            } else {
                notify(error.message || "행사 목록을 가져오는 중 오류가 발생했습니다.", "error");
            }
        } finally {
            setLoadingEvents(false);
        }
    };

    // size 는 방금 바꾼 페이지 크기를 바로 쓰려고 받는다 — setPageSize 는 다음 렌더에야 반영된다
    const fetchParticipants = async (eventName, page = 0, keyword = "", size = pageSize) => {
        if (!eventName) return;
        setLoadingParticipants(true);
        try {
            // 검색어를 서버로 넘겨 전체 참가자에 걸쳐 매칭(현재 페이지만 필터하던 버그 해소)
            const response = await qrEventsApi.getParticipantsPaginated(eventName, page, size, keyword);
            const list = response?.content ?? (Array.isArray(response) ? response : (response?.data ?? []));
            setParticipants(list);
            setTotalPages(response?.totalPages ?? 1);
            setTotalElements(response?.totalElements ?? list.length);
            setSelectedEvent(eventName);
            setCurrentPage(page);
            // 선택은 보고 있는 목록 기준이라, 목록이 바뀌면 비운다 (안 보이는 참가자가 선택된 채로 지워지지 않게)
            setSelected(new Set());
            onSelectedEventChange?.(eventName);
        } catch (error) {
            if (error.message?.includes("No permission") || error.message?.includes("권한")) {
                notify("참가자 목록 확인 권한이 없습니다", "error");
            } else {
                notify(error.message || "참가자 목록을 가져오는 중 오류가 발생했습니다.", "error");
            }
        } finally {
            setLoadingParticipants(false);
        }
    };

    useEffect(() => {
        fetchEvents();
        // eslint-disable-next-line
    }, []);

    // ✅ 상위에서 행사명이 들어오면 자동 선택 + 로드
    useEffect(() => {
        if (!selectedEventFromParent) return;
        if (selectedEventFromParent === selectedEvent) return;
        fetchParticipants(selectedEventFromParent, 0, participantSearch);
        // eslint-disable-next-line
    }, [selectedEventFromParent]);

    // 검색어 변경 → 디바운스 후 서버 재조회(page 0). 전체 참가자에 걸쳐 매칭되도록.
    // (sortedParticipants 의 클라이언트 필터는 디바운스 대기 중 현재 페이지에 즉시 반영되는 보조 역할)
    useEffect(() => {
        if (!selectedEvent) return;
        const t = setTimeout(() => { fetchParticipants(selectedEvent, 0, participantSearch); }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [participantSearch]);

    const selectedEventLabel = useMemo(() => selectedEvent || "(미선택)", [selectedEvent]);

    const [sort, setSort] = useState({ key: "name", dir: "asc" });

    const toggleSort = (key) =>
        setSort(s => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

    const sortLabel = (key, label) =>
        `${label}${sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : " ⇅"}`;

    // 검색 + 정렬 적용. 검색은 키워드 부분일치 (이름·이메일·소속·연락처·직함·시트명).
    const sortedParticipants = useMemo(() => {
        const kw = String(participantSearch || "").trim().toLowerCase();
        const filtered = !kw
            ? (participants || [])
            : (participants || []).filter((p) => {
                const hay = [p.name, p.email, p.affiliation, p.contactNumber, p.title, p.sheetName, p.privateCode]
                    .map((v) => String(v || "").toLowerCase())
                    .join(" ");
                // 연락처는 하이픈을 빼고도 비교 — 화면은 010-0000-0000 으로 보여주는데 저장값은 섞여 있다(서버 검색과 같은 규칙)
                const kwNoDash = kw.replace(/-/g, "");
                const phoneNoDash = String(p.contactNumber || "").replace(/-/g, "");
                return hay.includes(kw) || (kwNoDash !== "" && phoneNoDash.includes(kwNoDash));
            });
        return [...filtered].sort((a, b) => {
            const av = a[sort.key] ?? "";
            const bv = b[sort.key] ?? "";
            const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv), "ko");
            return sort.dir === "asc" ? cmp : -cmp;
        });
    }, [participants, sort, participantSearch]);

    const allOnPageSelected = sortedParticipants.length > 0 && sortedParticipants.every((p) => selected.has(p.id));

    const toggleSelect = (id) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    // 헤더 체크박스 — 지금 보이는 페이지의 참가자 전체
    const toggleSelectAll = () => setSelected(
        allOnPageSelected ? new Set() : new Set(sortedParticipants.map((p) => p.id))
    );

    const openDialog = (mode, participant = emptyParticipant) => {
        if (!selectedEvent && mode === "add") {
            notify("참가자를 추가하려면 먼저 행사를 선택해주세요.", "warning");
            return;
        }
        setDialogMode(mode);
        setCurrentParticipant({ ...participant });
        setOpenEditDialog(true);
    };

    const closeDialog = () => setOpenEditDialog(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentParticipant((prev) => ({ ...prev, [name]: value }));
    };

    const handlePhoneNumberChange = (e) => {
        let value = e.target.value.replace(/[^0-9]/g, "");
        if (value.length > 3 && value.length <= 7) {
            value = `${value.slice(0, 3)}-${value.slice(3)}`;
        } else if (value.length > 7) {
            value = `${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7, 11)}`;
        }
        setCurrentParticipant((prev) => ({ ...prev, contactNumber: value }));
    };

    // 이름만 필수 — 업로드된 참가자는 성별이 비었거나 연락처가 숫자만 있는 경우가 많아,
    // 나머지까지 필수로 두면 행을 눌러 고친 뒤 저장할 수가 없다. 이메일은 적었을 때만 형식을 본다.
    const validateParticipant = () => {
        if (!String(currentParticipant.name || "").trim()) {
            notify("이름을 입력해주세요.", "warning");
            return false;
        }

        const email = String(currentParticipant.email || "").trim();
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            notify("올바른 이메일 형식을 입력해주세요.", "warning");
            return false;
        }
        return true;
    };

    const handleSave = () => {
        if (!validateParticipant()) return;

        const isAdd = dialogMode === "add";
        return runApiAction({
            request: () => isAdd
                ? qrEventsApi.registerParticipant({ eventName: selectedEvent, participant: currentParticipant })
                : qrEventsApi.updateParticipant({ eventName: selectedEvent, participant: currentParticipant }),
            successMsg: isAdd ? "참가자가 추가되었습니다." : "참가자가 수정되었습니다.",
            errorMsg: serverMsgOr("존재하지 않는 행사이거나 중복 참가자(이메일+행사) 입니다."),
            onSuccess: async () => {
                await fetchParticipants(selectedEvent, currentPage, participantSearch);
                closeDialog();
            },
            onError: (error) => console.error("저장 오류", error),
        });
    };

    // 수정 모달 안의 삭제 버튼
    const handleDelete = async () => {
        const target = currentParticipant;
        if (!(await confirmDialog({ message: `${target.name} 참가자를 삭제하시겠습니까?\n출석 기록도 함께 삭제됩니다.` }))) return;
        await runApiAction({
            request: () => qrEventsApi.deleteParticipant(target.id),
            successMsg: "참가자가 삭제되었습니다.",
            errorMsg: serverMsgOr("참가자 삭제 중 오류가 발생했습니다."),
            onSuccess: async () => {
                closeDialog();
                await fetchParticipants(selectedEvent, currentPage, participantSearch);
            },
            onError: (error) => console.error("삭제 오류", error),
        });
    };

    const handleBulkDelete = async () => {
        if (selected.size === 0) return;
        const ids = [...selected];
        if (!(await confirmDialog({ message: `선택한 참가자 ${ids.length}명을 삭제하시겠습니까?\n출석 기록도 함께 삭제됩니다.` }))) return;
        // 이 페이지를 전부 지우면 빈 페이지가 남으니 앞 페이지로 간다
        const nextPage = ids.length >= participants.length && currentPage > 0 ? currentPage - 1 : currentPage;
        await runApiAction({
            request: () => qrEventsApi.bulkDeleteParticipants(selectedEvent, ids),
            successMsg: (data) => (typeof data === "string" ? data : "참가자가 삭제되었습니다."),
            errorMsg: serverMsgOr("참가자 삭제 중 오류가 발생했습니다."),
            onSuccess: () => fetchParticipants(selectedEvent, nextPage, participantSearch),
            onError: (error) => console.error("선택 삭제 오류", error),
        });
    };

    const handleOpenUpload = () => {
        if (!selectedEvent) {
            notify("xlsx 파일을 업로드하려면 먼저 행사를 선택해주세요.", "warning");
            return;
        }
        setFile(null);
        setUploadingXlsx(false);
        setOpenUploadDialog(true);
    };

    const handleUploadXlsx = async () => {
        if (uploadingXlsx) return;
        if (!file) {
            notify("xlsx 파일을 선택해주세요.", "warning");
            return;
        }

        await runApiAction({
            request: () => qrEventsApi.uploadParticipants(selectedEvent, file),
            errorMsg: serverMsgOr("xlsx 파일 업로드 중 오류가 발생했습니다."),
            successMsg: (data) => typeof data === "string" ? data : data?.message || "업로드 완료",
            setLoading: setUploadingXlsx,
            onSuccess: async () => {
                await fetchParticipants(selectedEvent, currentPage, participantSearch);
                setOpenUploadDialog(false);
            },
            onError: (error) => console.error("업로드 오류", error),
        });
    };

    const handleDownloadXlsx = async () => {
        if (!selectedEvent) {
            notify("다운로드하려면 먼저 행사를 선택해주세요.", "warning");
            return;
        }

        try {
            const res = await qrEventsApi.downloadParticipants(selectedEvent);
            const blob = new Blob([res instanceof Blob ? res : (res?.data ?? res)], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${selectedEvent}_participants.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("다운로드 오류", error);
            notify("참가자 xlsx 다운로드 중 오류가 발생했습니다.", "error");
        }
    };

    const handlePageChange = (newPage) => {
        // TablePaginator는 1-based, API는 0-based. 현재 검색어를 유지한 채 페이지 이동.
        fetchParticipants(selectedEvent, newPage - 1, participantSearch);
    };

    // 페이지 크기를 바꾸면 첫 페이지부터 다시 — 보던 페이지 번호는 크기가 달라지면 뜻이 없다
    const handlePageSizeChange = (size) => {
        setPageSize(size);
        fetchParticipants(selectedEvent, 0, participantSearch, size);
    };


    return (
        <Box>
            {/* 상단 컨트롤 */}
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ md: "center" }}
                justifyContent="space-between"
            >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "flex-end" }}>
                    <FormField label="행사 선택 (행사명 검색 가능)" required>
                        {/* 사내 공용 AppAutocomplete 로 통일 — 행사 수가 늘어도 검색으로 빠르게 찾도록.
                            value 는 객체(event) 로 controlled, selectedEvent state 는 그대로 eventName 문자열 유지
                            (다른 호출처들이 string eventName 을 사용하므로 변경 최소화). */}
                        <AppAutocomplete
                            options={events}
                            value={events.find((e) => e.eventName === selectedEvent) || null}
                            onChange={(v) => {
                                const name = v && typeof v !== "string" ? v.eventName : "";
                                if (name) fetchParticipants(name, 0, participantSearch);
                            }}
                            getOptionLabel={(o) => (typeof o === "string" ? o : (o?.eventName || ""))}
                            isOptionEqualToValue={(a, b) => {
                                const an = typeof a === "string" ? a : a?.eventName;
                                const bn = typeof b === "string" ? b : b?.eventName;
                                return an === bn;
                            }}
                            disabled={loadingEvents}
                            placeholder="행사를 선택하세요"
                            variant="outlined" disableUnderline={false}
                            sx={{ minWidth: 280 }}
                        />
                    </FormField>

                    <Typography sx={{ ...ds.typography.caption, pb: 0.8 }}>
                        선택된 행사: <b>{selectedEventLabel}</b>
                    </Typography>

                    {(loadingEvents || loadingParticipants) && <CircularProgress size={22} />}
                </Stack>

                <Stack direction="row" spacing={1}>
                    <ActionButton variant="download" onClick={handleDownloadXlsx} disabled={!selectedEvent}
                        label="전체 xlsx 다운로드" />
                    <ActionButton variant="upload" onClick={handleOpenUpload} disabled={!selectedEvent}
                        label="참가자 xlsx로 추가" />
                    <ActionButton variant="add" startIcon={<PersonAddAlt1Icon sx={{ fontSize: 16 }} />}
                        onClick={() => openDialog("add", emptyParticipant)} disabled={!selectedEvent}
                        label="참가자 추가" />
                </Stack>
            </Stack>

            {/* 테이블 */}
            <Paper variant="outlined" sx={{ ...ds.card, mt: 2, overflow: "hidden" }}>
                <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                        <Typography sx={ds.typography.sectionTitle}>참가자 목록</Typography>
                        {selected.size > 0 && (
                            <Box sx={{
                                display: "flex", alignItems: "center", gap: 1.5,
                                px: 2, py: 0.6, bgcolor: "#fef2f2", borderRadius: 1.5,
                            }}>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#dc2626" }}>
                                    {selected.size}명 선택됨
                                </Typography>
                                <ActionButton variant="secondary" onClick={handleBulkDelete}
                                    startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />}
                                    label="선택 삭제" sx={{ fontSize: 12, color: "#dc2626" }} />
                                <Chip label="선택 해제" size="small" onClick={() => setSelected(new Set())}
                                    sx={{ height: 22, fontSize: 11, cursor: "pointer" }} />
                            </Box>
                        )}
                        <Box sx={{ flex: 1 }} />
                        <TextField
                            size="small"
                            placeholder="이름·이메일·소속·연락처·직함 검색"
                            value={participantSearch}
                            onChange={(e) => setParticipantSearch(e.target.value)}
                            disabled={!selectedEvent}
                            sx={{ width: { xs: "100%", sm: 320 } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: participantSearch ? (
                                    <InputAdornment position="end">
                                        <Tooltip title="검색어 지우기">
                                            <IconButton size="small"
                                                onClick={() => setParticipantSearch("")}
                                                sx={{ p: 0.5 }}>
                                                <ClearIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        {participantSearch && (
                            <Typography sx={{ ...ds.typography.caption, whiteSpace: "nowrap" }}>
                                {totalElements}건 매칭
                            </Typography>
                        )}
                        <TableSizeSelect value={pageSize} onChange={handlePageSizeChange} options={PAGE_SIZE_OPTIONS} />
                    </Stack>
                </Box>
                <Divider />
                <TableContainer>
                    {/* 열 너비는 정해 두지 않는다 — 값 길이에 따라 정해지고, 줄바꿈 없이 넘치면 가로 스크롤 */}
                    <Table size="small" sx={[ds.tableSx, { "& th, & td": { whiteSpace: "nowrap" } }]}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    {/* 비활성 체크박스는 마우스 이벤트를 받지 않아 Tooltip 이 경고한다 — span 으로 감싼다 */}
                                    <Tooltip title="이 페이지 전체 선택">
                                        <span>
                                            <Checkbox size="small"
                                                checked={allOnPageSelected}
                                                indeterminate={selected.size > 0 && !allOnPageSelected}
                                                onChange={toggleSelectAll}
                                                disabled={sortedParticipants.length === 0} />
                                        </span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="center">
                                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                        <span>개인코드</span>
                                        <Tooltip title="QR 코드와 동일한 값이에요">
                                            <HelpOutlineIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("name")}>{sortLabel("name", "이름")}</TableCell>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("affiliation")}>{sortLabel("affiliation", "소속")}</TableCell>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("gender")}>{sortLabel("gender", "성별")}</TableCell>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("contactNumber")}>{sortLabel("contactNumber", "연락처")}</TableCell>
                                <TableCell>시트명</TableCell>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("email")}>{sortLabel("email", "이메일")}</TableCell>
                                <TableCell align="center" sx={{ cursor: "pointer" }} onClick={() => toggleSort("attendTime")}>
                                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                        <span>{sortLabel("attendTime", "참석 시간")}</span>
                                        <Tooltip title="최초 참석 시간을 의미해요">
                                            <HelpOutlineIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {sortedParticipants.map((p) => (
                                <TableRow key={p.id} hover selected={selected.has(p.id)}
                                    sx={{ cursor: "pointer" }}
                                    onClick={() => openDialog("edit", p)}>
                                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                        <Checkbox size="small" checked={selected.has(p.id)}
                                            onChange={() => toggleSelect(p.id)} />
                                    </TableCell>
                                    <TableCell align="center">{p.privateCode}</TableCell>
                                    <TableCell align="center">{p.name}</TableCell>
                                    <TableCell align="center">{p.affiliation}</TableCell>
                                    <TableCell align="center">{p.gender}</TableCell>
                                    <TableCell align="center">{formatPhoneDisplay(p.contactNumber)}</TableCell>
                                    <TableCell align="center">{p.sheetName}</TableCell>
                                    <TableCell align="center">{p.email}</TableCell>
                                    <TableCell align="center">{formatDateTime(p.attendTime)}</TableCell>
                                </TableRow>
                            ))}

                            {sortedParticipants.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                        {!selectedEvent
                                            ? "행사를 선택해주세요."
                                            : participantSearch
                                                ? "검색 조건에 맞는 참가자가 없습니다."
                                                : "참가자가 없습니다."}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
            <TablePaginator
                totalPages={totalPages}
                total={totalElements}
                pageSize={pageSize}
                page={currentPage + 1}
                onPageChange={handlePageChange}
            />
                </TableContainer>
            </Paper>

            {/* xlsx 업로드 다이얼로그 */}
            <FormModal
                open={openUploadDialog}
                onClose={() => setOpenUploadDialog(false)}
                title="xlsx 파일 업로드"
                subtitle={`선택된 행사: ${selectedEventLabel}`}
                maxWidth="sm"
                onSave={handleUploadXlsx}
                saving={uploadingXlsx}
                saveLabel={uploadingXlsx ? "업로드 중" : "업로드"}
                saveDisabled={!file}
            >
                <Stack spacing={1.5}>
                    <ActionButton variant="secondary"
                        component="label"
                        startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
                        disabled={uploadingXlsx}>
                        <Box component="span">파일 선택 (.xlsx)</Box>
                        <input hidden type="file" accept=".xlsx" disabled={uploadingXlsx} onChange={(e) => setFile(e.target.files?.[0] || null)} />
                    </ActionButton>

                    <Typography variant="body2">
                        {file ? `선택된 파일: ${file.name}` : "선택된 파일 없음"}
                    </Typography>
                </Stack>
            </FormModal>

            {/* 참가자 추가/수정 다이얼로그 — 수정일 때만 왼쪽 아래에 삭제 */}
            <FormModal
                open={openEditDialog}
                onClose={closeDialog}
                title={dialogMode === "add" ? "참가자 추가" : "참가자 수정"}
                maxWidth="sm"
                onSave={handleSave}
                saveLabel="저장"
                footerExtras={dialogMode === "edit" ? (
                    <ActionButton variant="delete" onClick={handleDelete} label="삭제" />
                ) : null}
            >
                <Stack spacing={2}>
                    <FormField label="개인코드">
                        {dialogMode === "add" ? (
                            <TextField name="privateCode" size="small" value={currentParticipant.privateCode || ""} onChange={handleInputChange}
                                placeholder="비우면 자동으로 만들어져요" helperText="같은 행사 안에서는 겹칠 수 없어요" fullWidth />
                        ) : (
                            <TextField size="small" value={currentParticipant.privateCode || ""} disabled fullWidth />
                        )}
                    </FormField>
                    <FormField label="이름" required>
                        <TextField name="name" size="small" value={currentParticipant.name || ""} onChange={handleInputChange} placeholder="이름을 입력하세요" fullWidth />
                    </FormField>
                    <FormField label="소속">
                        <TextField name="affiliation" size="small" value={currentParticipant.affiliation || ""} onChange={handleInputChange} placeholder="소속을 입력하세요" fullWidth />
                    </FormField>
                    <FormField label="성별">
                        <TextField name="gender" size="small" value={currentParticipant.gender || ""} onChange={handleInputChange} placeholder="Male / Female / Other" fullWidth />
                    </FormField>
                    <FormField label="연락처">
                        <TextField name="contactNumber" size="small" value={currentParticipant.contactNumber || ""} onChange={handlePhoneNumberChange} placeholder="010-0000-0000" fullWidth />
                    </FormField>
                    <FormField label="이메일">
                        <TextField name="email" size="small" value={currentParticipant.email || ""} onChange={handleInputChange} placeholder="이메일을 입력하세요"
                            helperText="비워 두면 임시 주소(remail.…@gmail.com)로 저장돼요" fullWidth />
                    </FormField>
                </Stack>
            </FormModal>

        </Box>
    );
}
